/**
 * How a search of training history is read, and which sets it finds — to the number, per
 * `/specs/features/search.spec.md`. The web app reads its searches through this; the Android app
 * carries a port in `lib/SetSearch.kt`, and both answer `corpus/corpus.json`.
 *
 * A search is read once into terms and each set is then asked whether every term holds for it,
 * so a history of thousands of sets costs one reading and a pass, not a reading per set.
 */

export type WeightUnits = 'kg' | 'lb'

/** A song played while a set was performed, as much of it as a search reads. */
export interface SearchableSong {
  readonly title: string
  readonly artist?: string | null
  readonly album?: string | null
}

/** What a search can ask of a set: what it says, its numbers, and when it was performed. */
export interface SearchableSet {
  readonly exerciseName: string
  readonly notes: string | null
  readonly weightKg: number | null
  readonly reps: number | null
  readonly durationSeconds: number | null
  readonly distanceMeters: number | null
  readonly rpe: number | null
  /** When it was performed, in milliseconds since the epoch. */
  readonly performedAt: number
  readonly songs?: readonly SearchableSong[]
  /** The workout it was part of: its name, and the notes written before and after it. */
  readonly workoutName?: string | null
  readonly workoutNotes?: readonly (string | null)[]
  /** The template that workout was played from, and the template's workout it was. */
  readonly templateName?: string | null
  readonly templateWorkout?: string | null
}

/** How a search is read on this device. */
export interface ReadingOptions {
  /** Whether the device writes a date with the day before the month: `13/9` rather than `9/13`. */
  readonly dayFirst: boolean
}

/** What a set is asked against on this device: its units, its clock and its calendar. */
export interface SearchContext {
  /** The account's weight units, which a bare number is read in. */
  readonly units: WeightUnits
  /** Now, in milliseconds since the epoch — what `today` and `last week` count from. */
  readonly now: number
  /** The device's time zone, as an IANA name, which a moment is read in. */
  readonly timeZone: string
  /** The day the device begins a week on, 1 for Monday through 7 for Sunday. */
  readonly firstDayOfWeek: number
  /** The account's distance units, which a `distance:` with no unit is read in. Kilometres where unsaid. */
  readonly distanceUnits?: 'km' | 'mi'
}

/**
 * Where a number may fall: between two ends, either of which may be open. An exact number is a
 * bound whose ends are both that number.
 */
interface Bound {
  readonly low: number
  readonly high: number
  /** `>` and `<` leave their own number out; everything else keeps it in. */
  readonly lowOpen: boolean
  readonly highOpen: boolean
}

/** What a number is a number of, where anything says. */
type Kind = 'bare' | 'weight' | 'reps' | 'time' | 'distance' | 'metresOrMinutes' | 'effort'

interface Operand {
  readonly kind: Kind
  /** The unit it was typed in: `kg`, `lb`, `s`, `min`, `h`, `km`, `mi`, `m`, `yd`, or none. */
  readonly unit: string
  readonly bound: Bound
}

/** Reps at a weight — the weight in the unit it was typed in, or the account's where none. */
interface Pairing {
  readonly reps: Bound
  readonly weight: Bound
  readonly unit: WeightUnits | null
}

/** When a set was performed, said the way a person says it — see the spec, Moment. */
type Moment =
  | { readonly type: 'month'; readonly month: number; readonly year: number | null }
  | {
      readonly type: 'date'
      readonly day: number
      readonly month: number
      readonly year: number | null
    }
  | { readonly type: 'year'; readonly year: number }
  | { readonly type: 'weekday'; readonly weekday: number }
  | { readonly type: 'partOfDay'; readonly part: PartOfDay }
  | { readonly type: 'daysAgo' | 'weeksAgo' | 'monthsAgo' | 'yearsAgo'; readonly count: number }

type PartOfDay = 'morning' | 'afternoon' | 'evening' | 'night'

/** The labels a search reads — see the spec, Labels. */
const TEXT_LABELS = ['exercise', 'note', 'song', 'artist', 'album', 'workout', 'template'] as const
type TextLabel = (typeof TEXT_LABELS)[number]
const MEASURE_LABELS = ['weight', 'reps', 'time', 'distance', 'rpe'] as const
type MeasureLabel = (typeof MEASURE_LABELS)[number]
const LABELS: ReadonlySet<string> = new Set([
  ...TEXT_LABELS,
  ...MEASURE_LABELS,
  'on',
  'after',
  'before',
  'is',
])

/** One day of the calendar, a month or a year — what `after:` and `before:` are given. */
interface Edge {
  readonly year: number
  readonly month: number | null
  readonly day: number | null
}

type Reading =
  | { readonly kind: 'word'; readonly text: string }
  | { readonly kind: 'field'; readonly field: TextLabel; readonly text: string }
  | {
      readonly kind: 'edge'
      readonly edge: 'after' | 'before'
      readonly on: Edge
      readonly text: string
    }
  | { readonly kind: 'moment'; readonly moment: Moment; readonly text: string }
  | { readonly kind: 'bodyweight'; readonly text: string }
  | { readonly kind: 'measure'; readonly operand: Operand; readonly text: string }
  | {
      readonly kind: 'joined'
      /** Any one of these, where the numbers pair reps with a weight at all. */
      readonly pairings: readonly Pairing[]
      /** And every one of these: the times, distances and efforts joined in. */
      readonly beside: readonly Operand[]
      readonly text: string
    }

/**
 * One thing a search asks of a set, with the label it was narrowed by, whether it was negated,
 * and where it was written in the search — which is what lets a filter take its own back out.
 */
export type Term = Reading & {
  readonly label: string | null
  readonly negated: boolean
  /** The whole of it in the search, `-` and label and quotes included; null inside plain text. */
  readonly span: Span | null
  /** Its value in the search, quotes aside; null where it carries no label. */
  readonly valueSpan: Span | null
}

const KG_PER_LB = 0.45359237
const METERS_PER_MILE = 1609.344
const METERS_PER_YARD = 0.9144

const UNITS: Readonly<Record<string, { kind: Kind; unit: string }>> = {
  kg: { kind: 'weight', unit: 'kg' },
  kgs: { kind: 'weight', unit: 'kg' },
  kilo: { kind: 'weight', unit: 'kg' },
  kilos: { kind: 'weight', unit: 'kg' },
  kilogram: { kind: 'weight', unit: 'kg' },
  kilograms: { kind: 'weight', unit: 'kg' },
  lb: { kind: 'weight', unit: 'lb' },
  lbs: { kind: 'weight', unit: 'lb' },
  pound: { kind: 'weight', unit: 'lb' },
  pounds: { kind: 'weight', unit: 'lb' },
  rep: { kind: 'reps', unit: '' },
  reps: { kind: 'reps', unit: '' },
  s: { kind: 'time', unit: 's' },
  sec: { kind: 'time', unit: 's' },
  secs: { kind: 'time', unit: 's' },
  second: { kind: 'time', unit: 's' },
  seconds: { kind: 'time', unit: 's' },
  min: { kind: 'time', unit: 'min' },
  mins: { kind: 'time', unit: 'min' },
  minute: { kind: 'time', unit: 'min' },
  minutes: { kind: 'time', unit: 'min' },
  h: { kind: 'time', unit: 'h' },
  hr: { kind: 'time', unit: 'h' },
  hrs: { kind: 'time', unit: 'h' },
  hour: { kind: 'time', unit: 'h' },
  hours: { kind: 'time', unit: 'h' },
  km: { kind: 'distance', unit: 'km' },
  kms: { kind: 'distance', unit: 'km' },
  kilometre: { kind: 'distance', unit: 'km' },
  kilometres: { kind: 'distance', unit: 'km' },
  kilometer: { kind: 'distance', unit: 'km' },
  kilometers: { kind: 'distance', unit: 'km' },
  mi: { kind: 'distance', unit: 'mi' },
  mile: { kind: 'distance', unit: 'mi' },
  miles: { kind: 'distance', unit: 'mi' },
  metre: { kind: 'distance', unit: 'm' },
  metres: { kind: 'distance', unit: 'm' },
  meter: { kind: 'distance', unit: 'm' },
  meters: { kind: 'distance', unit: 'm' },
  yd: { kind: 'distance', unit: 'yd' },
  yds: { kind: 'distance', unit: 'yd' },
  yard: { kind: 'distance', unit: 'yd' },
  yards: { kind: 'distance', unit: 'yd' },
  m: { kind: 'metresOrMinutes', unit: 'm' },
}

/** The unit names, longest first, so `mins` is read before `min` and `min` before `mi`. */
const UNIT_NAMES = Object.keys(UNITS).sort((a, b) => b.length - a.length)

type Token =
  | { readonly t: 'num'; readonly value: number; readonly clock: boolean; readonly at: Span }
  | { readonly t: 'unit'; readonly name: string; readonly at: Span }
  | { readonly t: 'x' | 'dash' | 'plus' | 'at' | 'rpe' | 'bw' | 'slash'; readonly at: Span }
  | { readonly t: 'op'; readonly op: '>' | '<' | '>=' | '<='; readonly at: Span }
  | { readonly t: 'word'; readonly at: Span }

export interface Span {
  readonly start: number
  readonly end: number
}

/** The search as it is read: lower case, one joining sign, one dash, and a point for a fraction. */
function normalized(text: string): string {
  return text
    .toLowerCase()
    .replace(/[×*]/g, 'x')
    .replace(/[–—]/g, '-')
    .replace(/[“”„]/g, '"')
    .replace(/(\d),(\d)/g, '$1.$2')
}

/** What a name, a note or a term is compared as: normalized, and with no spaces in it. */
function compact(text: string): string {
  return normalized(text).replace(/\s+/g, '')
}

const isDigit = (c: string | undefined) => c !== undefined && c >= '0' && c <= '9'
const isSpace = (c: string | undefined) => c !== undefined && /\s/.test(c)

/** A run of digits, with a fraction or a clock's minutes and seconds after it. */
function readNumber(s: string, from: number): { token: Token; end: number } {
  let i = from
  while (isDigit(s[i])) i++
  if (s[i] === '.' && isDigit(s[i + 1])) {
    i++
    while (isDigit(s[i])) i++
  }
  const parts = [s.slice(from, i)]
  while (parts.length < 3 && s[i] === ':' && isDigit(s[i + 1]) && isDigit(s[i + 2])) {
    parts.push(s.slice(i + 1, i + 3))
    i += 3
  }
  const clock = parts.length > 1
  const value = clock
    ? parts.reduce((total, part) => total * 60 + Number(part), 0)
    : Number(parts[0])
  return { token: { t: 'num', value, clock, at: { start: from, end: i } }, end: i }
}

const SIGNS: readonly (readonly [string, Token['t'], ('>' | '<' | '>=' | '<=')?])[] = [
  ['>=', 'op', '>='],
  ['<=', 'op', '<='],
  ['>', 'op', '>'],
  ['<', 'op', '<'],
  ['@', 'at'],
  ['+', 'plus'],
  ['-', 'dash'],
  ['/', 'slash'],
]

/**
 * A run of everything that is not a space or a digit, taken apart into what a measure is made
 * of — signs, a unit, a joining `x`, `rpe`, `bw` — and whatever is left, which is a word.
 */
function readRun(s: string, start: number, end: number, into: Token[]): void {
  let i = start
  while (i < end) {
    const sign = SIGNS.find(([text]) => s.startsWith(text, i) && i + text.length <= end)
    if (sign !== undefined) {
      const [text, t, op] = sign
      const at = { start: i, end: i + text.length }
      into.push(op === undefined ? ({ t, at } as Token) : { t: 'op', op, at })
      i += text.length
      continue
    }
    if (s[i] === ',' || s[i] === ';') {
      i++
      continue
    }
    const rest = s.slice(i, end)
    /*
     * Something standing on its own up to the end of the run, or up to a sign or a joining x:
     * a unit, `x`, `rpe`, `bw`. Followed by any other letter it is part of a word — `mine`
     * holds `min` and `sit` holds `s`, and neither is a unit.
     */
    const standing = (length: number) => {
      const after = rest.slice(length)
      return (
        after === '' ||
        SIGNS.some(([text]) => after.startsWith(text)) ||
        after.startsWith(',') ||
        after === 'x' ||
        (after.startsWith('x') && SIGNS.some(([text]) => after.slice(1).startsWith(text)))
      )
    }
    if (rest.startsWith('x') && standing(1)) {
      into.push({ t: 'x', at: { start: i, end: i + 1 } })
      i += 1
      continue
    }
    const keyword = (['bodyweight', 'rpe', 'bw'] as const).find(
      (name) => rest.startsWith(name) && standing(name.length),
    )
    if (keyword !== undefined) {
      into.push({ t: keyword === 'rpe' ? 'rpe' : 'bw', at: { start: i, end: i + keyword.length } })
      i += keyword.length
      continue
    }
    const unit = UNIT_NAMES.find((name) => rest.startsWith(name) && standing(name.length))
    if (unit !== undefined) {
      into.push({ t: 'unit', name: unit, at: { start: i, end: i + unit.length } })
      i += unit.length
      continue
    }
    into.push({ t: 'word', at: { start: i, end } })
    return
  }
}

function tokens(s: string): Token[] {
  const out: Token[] = []
  let i = 0
  while (i < s.length) {
    if (isSpace(s[i])) {
      i++
    } else if (isDigit(s[i])) {
      const read = readNumber(s, i)
      out.push(read.token)
      i = read.end
    } else {
      let end = i
      while (end < s.length && !isSpace(s[end]) && !isDigit(s[end])) end++
      readRun(s, i, end, out)
      i = end
    }
  }
  return out
}

const exactly = (value: number): Bound => ({
  low: value,
  high: value,
  lowOpen: false,
  highOpen: false,
})

/** One number and what it is of, read from `at`, or null where no measure starts there. */
function readOperand(
  toks: readonly Token[],
  at: number,
): { operand: Operand; next: number } | null {
  let j = at
  const first = toks[j]
  if (first?.t === 'at' || first?.t === 'rpe') {
    const low = toks[j + 1]
    if (low?.t !== 'num' || low.clock) return null
    j += 2
    let bound = exactly(low.value)
    const dash = toks[j]
    const high = toks[j + 1]
    if (dash?.t === 'dash' && high?.t === 'num' && !high.clock) {
      bound = rangeOf(low.value, high.value)
      j += 2
    }
    return { operand: { kind: 'effort', unit: '', bound }, next: j }
  }
  let op: '>' | '<' | '>=' | '<=' | null = null
  if (first?.t === 'op') {
    op = first.op
    j++
  }
  const low = toks[j]
  if (low?.t !== 'num') return null
  j++
  let bound = exactly(low.value)
  let clock = low.clock
  const dash = toks[j]
  const high = toks[j + 1]
  if (op === null && dash?.t === 'dash' && high?.t === 'num') {
    bound = rangeOf(low.value, high.value)
    clock = clock || high.clock
    j += 2
  } else if (op !== null) {
    bound = boundOf(op, low.value)
  }
  let plus = false
  if (op === null && bound.low === bound.high && toks[j]?.t === 'plus') {
    plus = true
    j++
  }
  let kind: Kind = clock ? 'time' : 'bare'
  let unit = clock ? 's' : ''
  const named = toks[j]
  if (named?.t === 'unit') {
    const read = UNITS[named.name]
    // A clock is a time whatever follows it, so only a time's unit is taken with it.
    if (read !== undefined && (!clock || read.kind === 'time')) {
      if (!clock) {
        kind = read.kind
        unit = read.unit
      }
      j++
    }
  }
  if (op === null && !plus && bound.low === bound.high && toks[j]?.t === 'plus') {
    plus = true
    j++
  }
  if (plus) {
    bound = boundOf('>=', bound.low)
  }
  return { operand: { kind, unit, bound }, next: j }
}

function rangeOf(a: number, b: number): Bound {
  return { low: Math.min(a, b), high: Math.max(a, b), lowOpen: false, highOpen: false }
}

function boundOf(op: '>' | '<' | '>=' | '<=', value: number): Bound {
  switch (op) {
    case '>':
      return { low: value, high: Infinity, lowOpen: true, highOpen: false }
    case '>=':
      return { low: value, high: Infinity, lowOpen: false, highOpen: false }
    case '<':
      return { low: -Infinity, high: value, lowOpen: false, highOpen: true }
    case '<=':
      return { low: -Infinity, high: value, lowOpen: false, highOpen: false }
  }
}

/** The words a stretch of the search is, split on its spaces. */
function wordsOf(s: string, span: Span): Reading[] {
  return s
    .slice(span.start, span.end)
    .split(/\s+/)
    .filter((word) => word !== '')
    .map((text) => ({ kind: 'word', text }))
}

/** Where a bound stands, for telling the largest of several numbers joined together. */
const sizeOf = (bound: Bound) => (Number.isFinite(bound.high) ? bound.high : bound.low)

/** Numbers joined by `x`, read into what they ask — see the spec, Reading a search. */
function joinedTerm(s: string, operands: readonly Operand[], span: Span): Reading[] {
  const text = s.slice(span.start, span.end)
  const weights = operands.filter((operand) => operand.kind === 'weight')
  const repsOnes = operands.filter((operand) => operand.kind === 'reps')
  if (weights.length > 1 || repsOnes.length > 1) {
    return wordsOf(s, span)
  }
  const beside = operands.filter(
    (operand) => operand.kind !== 'bare' && operand.kind !== 'weight' && operand.kind !== 'reps',
  )
  /** The bare numbers right against the one at [index], either side of it. */
  const bareAround = (index: number) =>
    [operands[index - 1], operands[index + 1]].filter(
      (operand): operand is Operand => operand?.kind === 'bare',
    )
  const unitOf = (operand: Operand): WeightUnits => (operand.unit === 'lb' ? 'lb' : 'kg')

  let pairings: Pairing[] = []
  const weight = weights[0]
  const reps = repsOnes[0]
  if (weight !== undefined && reps !== undefined) {
    pairings = [{ reps: reps.bound, weight: weight.bound, unit: unitOf(weight) }]
  } else if (weight !== undefined) {
    const around = bareAround(operands.indexOf(weight))
    if (around.length === 0) {
      return [{ kind: 'joined', pairings: [], beside: [weight, ...beside], text }]
    }
    pairings = around.map((each) => ({
      reps: each.bound,
      weight: weight.bound,
      unit: unitOf(weight),
    }))
  } else if (reps !== undefined) {
    const around = bareAround(operands.indexOf(reps))
    if (around.length === 0) {
      return [{ kind: 'joined', pairings: [], beside: [reps, ...beside], text }]
    }
    pairings = around.map((each) => ({ reps: reps.bound, weight: each.bound, unit: null }))
  } else if (beside.length === 0) {
    const bares = operands
    const [a, b] = bares
    if (bares.length === 2 && a !== undefined && b !== undefined) {
      pairings = [
        { reps: a.bound, weight: b.bound, unit: null },
        { reps: b.bound, weight: a.bound, unit: null },
      ]
    } else if (bares.length > 2) {
      const largest = bares.reduce(
        (held, each, index) =>
          sizeOf(each.bound) > sizeOf(bares[held]?.bound ?? each.bound) ? index : held,
        0,
      )
      const heaviest = bares[largest]
      if (heaviest !== undefined) {
        pairings = bareAround(largest).map((each) => ({
          reps: each.bound,
          weight: heaviest.bound,
          unit: null,
        }))
      }
    }
  }
  return [{ kind: 'joined', pairings, beside, text }]
}

const MONTHS: Readonly<Record<string, number>> = Object.fromEntries(
  [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
  ].flatMap((name, index) => [
    [name, index + 1],
    [name.slice(0, 3), index + 1],
  ]),
)

const MONTH_NAMES: Readonly<Record<string, number>> = { ...MONTHS, sept: 9 }

const WEEKDAYS: Readonly<Record<string, number>> = Object.fromEntries(
  ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].flatMap(
    (name, index) => [
      [name, index + 1],
      [`${name}s`, index + 1],
      [name.slice(0, 3), index + 1],
    ],
  ),
)

const WEEKDAY_NAMES: Readonly<Record<string, number>> = {
  ...WEEKDAYS,
  tues: 2,
  weds: 3,
  thur: 4,
  thurs: 4,
}

const PARTS_OF_DAY: Readonly<Record<string, PartOfDay>> = {
  morning: 'morning',
  mornings: 'morning',
  afternoon: 'afternoon',
  afternoons: 'afternoon',
  evening: 'evening',
  evenings: 'evening',
  night: 'night',
  nights: 'night',
}

const COUNTED: Readonly<Record<string, 'daysAgo' | 'weeksAgo' | 'monthsAgo'>> = {
  day: 'daysAgo',
  days: 'daysAgo',
  week: 'weeksAgo',
  weeks: 'weeksAgo',
  month: 'monthsAgo',
  months: 'monthsAgo',
}

const CALENDAR: Readonly<Record<string, 'weeksAgo' | 'monthsAgo' | 'yearsAgo'>> = {
  week: 'weeksAgo',
  month: 'monthsAgo',
  year: 'yearsAgo',
}

const ORDINALS = new Set(['st', 'nd', 'rd', 'th'])

/** How many days a month can have, in a year where one is said and in any year where not. */
function daysIn(month: number, year: number | null): number {
  if (month === 2) {
    return year === null || (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)) ? 29 : 28
  }
  return [4, 6, 9, 11].includes(month) ? 30 : 31
}

/** A whole number, where the token is one — the only kind a day, a month or a year is. */
function wholeOf(token: Token | undefined): number | null {
  return token?.t === 'num' && !token.clock && Number.isInteger(token.value) ? token.value : null
}

/** A year written after a date or a month: four figures in the years a search reads, or two. */
function yearOf(s: string, token: Token | undefined): number | null {
  const value = wholeOf(token)
  if (value === null || token === undefined) return null
  const figures = s.slice(token.at.start, token.at.end).length
  if (figures === 4 && value >= 1900 && value <= 2099) return value
  if (figures === 2) return 2000 + value
  return null
}

/** Whether a whole number could begin a measure rather than a moment: a unit, an x or a sign after it. */
function measuredAfter(toks: readonly Token[], at: number): boolean {
  const next = toks[at]
  return (
    next !== undefined &&
    (next.t === 'unit' || next.t === 'x' || next.t === 'plus' || next.t === 'dash')
  )
}

/** A moment starting at [at], or null where none does — see the spec, Reading a search. */
function readMoment(
  s: string,
  toks: readonly Token[],
  at: number,
  options: ReadingOptions,
): { moment: Moment; next: number } | null {
  const wordAt = (k: number): string | null => {
    const token = toks[k]
    return token?.t === 'word' ? s.slice(token.at.start, token.at.end) : null
  }
  const first = wordAt(at)
  if (first === 'today') return { moment: { type: 'daysAgo', count: 0 }, next: at + 1 }
  if (first === 'yesterday') return { moment: { type: 'daysAgo', count: 1 }, next: at + 1 }
  if (first === 'this' || first === 'last') {
    const unit = CALENDAR[wordAt(at + 1) ?? '']
    if (unit !== undefined) {
      return { moment: { type: unit, count: first === 'this' ? 0 : 1 }, next: at + 2 }
    }
  }
  const weekday = first === null ? undefined : WEEKDAY_NAMES[first]
  if (weekday !== undefined) return { moment: { type: 'weekday', weekday }, next: at + 1 }
  const part = first === null ? undefined : PARTS_OF_DAY[first]
  if (part !== undefined) return { moment: { type: 'partOfDay', part }, next: at + 1 }

  const month = first === null ? undefined : MONTH_NAMES[first]
  if (month !== undefined) {
    // `sep 13`, `sep 13th 2025` — a day after the month's name, where nothing makes it a measure.
    const day = wholeOf(toks[at + 1])
    if (day !== null && day >= 1 && day <= 31 && !measuredAfter(toks, at + 2)) {
      let next = at + 2
      if (ORDINALS.has(wordAt(next) ?? '')) next++
      const year = yearOf(s, toks[next])
      if (day <= daysIn(month, year)) {
        return {
          moment: { type: 'date', day, month, year },
          next: year === null ? next : next + 1,
        }
      }
    }
    const year = yearOf(s, toks[at + 1])
    const withYear =
      year !== null && s.slice(toks[at + 1]?.at.start ?? 0, toks[at + 1]?.at.end ?? 0).length === 4
    return {
      moment: { type: 'month', month, year: withYear ? year : null },
      next: withYear ? at + 2 : at + 1,
    }
  }

  const lead = wholeOf(toks[at])
  if (lead === null) return null
  // `3 days ago`, `2 weeks ago`.
  const counted = COUNTED[wordAt(at + 1) ?? '']
  if (counted !== undefined && wordAt(at + 2) === 'ago') {
    return { moment: { type: counted, count: lead }, next: at + 3 }
  }
  // `13/9`, `13/9/2025` — in the order the device writes a date.
  if (toks[at + 1]?.t === 'slash') {
    const second = wholeOf(toks[at + 2])
    if (second !== null) {
      const [day, month] = options.dayFirst ? [lead, second] : [second, lead]
      const year = toks[at + 3]?.t === 'slash' ? yearOf(s, toks[at + 4]) : null
      if (month >= 1 && month <= 12 && day >= 1 && day <= daysIn(month, year)) {
        return {
          moment: { type: 'date', day, month, year },
          next: year === null ? at + 3 : at + 5,
        }
      }
    }
    return null
  }
  // `13 sep`, `13th sept 2025`.
  let after = at + 1
  if (ORDINALS.has(wordAt(after) ?? '')) after++
  const named = MONTH_NAMES[wordAt(after) ?? '']
  if (named !== undefined && lead >= 1 && lead <= 31) {
    const year = yearOf(s, toks[after + 1])
    if (lead <= daysIn(named, year)) {
      return {
        moment: { type: 'date', day: lead, month: named, year },
        next: year === null ? after + 1 : after + 2,
      }
    }
  }
  return null
}

/** A stretch of plain words and numbers, read the way a search always was. */
function readFree(s: string, options: ReadingOptions): Reading[] {
  const toks = tokens(s)
  const terms: Reading[] = []
  let i = 0
  while (i < toks.length) {
    const token = toks[i]
    if (token === undefined) break
    const moment = readMoment(s, toks, i, options)
    if (moment !== null) {
      const end = toks[moment.next - 1]?.at.end ?? token.at.end
      terms.push({ kind: 'moment', moment: moment.moment, text: s.slice(token.at.start, end) })
      i = moment.next
      continue
    }
    if (token.t === 'bw') {
      terms.push({ kind: 'bodyweight', text: s.slice(token.at.start, token.at.end) })
      i++
      continue
    }
    const leadingX = token.t === 'x' && readOperand(toks, i + 1) !== null
    const first = readOperand(toks, leadingX ? i + 1 : i)
    if (first === null) {
      terms.push({ kind: 'word', text: s.slice(token.at.start, token.at.end) })
      i++
      continue
    }
    const operands = [first.operand]
    let j = first.next
    let trailingX = false
    while (toks[j]?.t === 'x') {
      const next = readOperand(toks, j + 1)
      if (next === null) {
        trailingX = true
        j++
        break
      }
      operands.push(next.operand)
      j = next.next
    }
    const span = { start: token.at.start, end: toks[j - 1]?.at.end ?? token.at.end }
    const text = s.slice(span.start, span.end)
    const only = operands[0]
    if (operands.length === 1 && only !== undefined) {
      const dangling = leadingX || trailingX
      terms.push({
        kind: 'measure',
        operand:
          dangling && (only.kind === 'bare' || only.kind === 'reps')
            ? { kind: 'reps', unit: '', bound: only.bound }
            : only,
        text,
      })
    } else {
      terms.push(...joinedTerm(s, operands, span))
    }
    i = j
  }
  return terms
}

/** A moment a label's whole value is, or null where the value is not one. */
function momentOf(value: string, options: ReadingOptions): Moment | null {
  const toks = tokens(value)
  const read = readMoment(value, toks, 0, options)
  if (read !== null) return read.next === toks.length ? read.moment : null
  const year = toks.length === 1 ? yearOf(value, toks[0]) : null
  return year !== null && value.length === 4 ? { type: 'year', year } : null
}

/** A measure a label's whole value is, as the label reads it, or null where it is not one. */
function measureOf(label: MeasureLabel, value: string): Operand | null {
  const toks = tokens(value)
  const read = readOperand(toks, 0)
  if (read?.next !== toks.length) return null
  const { kind, bound } = read.operand
  switch (label) {
    case 'weight':
      return kind === 'weight'
        ? read.operand
        : kind === 'bare'
          ? { kind: 'weight', unit: '', bound }
          : null
    case 'reps':
      return kind === 'reps' || kind === 'bare' ? { kind: 'reps', unit: '', bound } : null
    case 'time':
      if (kind === 'time') return read.operand
      if (kind === 'bare') return { kind: 'time', unit: 's', bound }
      return kind === 'metresOrMinutes' ? { kind: 'time', unit: 'min', bound } : null
    case 'distance':
      if (kind === 'distance') return read.operand
      if (kind === 'bare') return { kind: 'distance', unit: '', bound }
      return kind === 'metresOrMinutes' ? { kind: 'distance', unit: 'm', bound } : null
    case 'rpe':
      return kind === 'effort' || kind === 'bare' ? { kind: 'effort', unit: '', bound } : null
  }
}

/** What a label reads its value as — see the spec, Labels — or null where it cannot read it. */
function readLabel(label: string, value: string, options: ReadingOptions): Reading | null {
  if (value === '') return null
  if ((TEXT_LABELS as readonly string[]).includes(label)) {
    return { kind: 'field', field: label as TextLabel, text: value }
  }
  if ((MEASURE_LABELS as readonly string[]).includes(label)) {
    const operand = measureOf(label as MeasureLabel, value)
    return operand === null ? null : { kind: 'measure', operand, text: value }
  }
  if (label === 'is') {
    return value === 'bodyweight' || value === 'bw' ? { kind: 'bodyweight', text: value } : null
  }
  const moment = momentOf(value, options)
  if (moment === null) return null
  if (label === 'on') return { kind: 'moment', moment, text: value }
  const on: Edge | null =
    moment.type === 'year'
      ? { year: moment.year, month: null, day: null }
      : moment.type === 'month' && moment.year !== null
        ? { year: moment.year, month: moment.month, day: null }
        : moment.type === 'date' && moment.year !== null
          ? { year: moment.year, month: moment.month, day: moment.day }
          : null
  return on === null
    ? null
    : { kind: 'edge', edge: label === 'after' ? 'after' : 'before', on, text: value }
}

/** A search, read into the terms every set it finds has to hold. */
export function readSearch(search: string, options: ReadingOptions = { dayFirst: true }): Term[] {
  const s = normalized(search)
  const terms: Term[] = []
  const plain = (reading: Reading): Term => ({
    ...reading,
    label: null,
    negated: false,
    span: null,
    valueSpan: null,
  })
  /** Where the stretch of plain words being gathered began, or -1 where none is. */
  let freeFrom = -1
  let freeTo = -1
  const flush = () => {
    if (freeFrom >= 0) {
      terms.push(...readFree(s.slice(freeFrom, freeTo), options).map(plain))
    }
    freeFrom = -1
  }
  let i = 0
  while (i < s.length) {
    if (isSpace(s[i])) {
      i++
      continue
    }
    const start = i
    // A `-` at the very start of a term negates it; one standing alone is a word.
    const negated = s[i] === '-' && i + 1 < s.length && !isSpace(s[i + 1])
    let at = negated ? i + 1 : i
    const named = /^([a-z]+):(?=\S)/.exec(s.slice(at))
    const label = named !== null && LABELS.has(named[1] ?? '') ? (named[1] ?? null) : null
    if (label !== null) at += label.length + 1
    const quoted = s[at] === '"'
    let valueStart = at
    let end: number
    let valueEnd: number
    if (quoted) {
      valueStart = at + 1
      const closing = s.indexOf('"', valueStart)
      valueEnd = closing < 0 ? s.length : closing
      end = closing < 0 ? s.length : closing + 1
    } else {
      end = at
      while (end < s.length && !isSpace(s[end])) end++
      valueEnd = end
    }
    if (!negated && label === null && !quoted) {
      // Plain: gathered with its neighbours, since a measure or a moment can span spaces.
      if (freeFrom < 0) freeFrom = start
      freeTo = end
      i = end
      continue
    }
    flush()
    const value = s.slice(valueStart, valueEnd).trim()
    const span = { start, end }
    const valueSpan = { start: valueStart, end: valueEnd }
    let readings: Reading[]
    if (label !== null) {
      const read = readLabel(label, value, options)
      readings = [
        read ?? { kind: 'word', text: s.slice(negated ? start + 1 : start, end).replace(/"/g, '') },
      ]
    } else if (quoted) {
      readings = value === '' ? [] : [{ kind: 'word', text: value }]
    } else {
      readings = readFree(value, options)
    }
    const labelled = label !== null && readings[0]?.kind !== 'word'
    for (const reading of readings) {
      terms.push({
        ...reading,
        label: labelled ? label : null,
        negated,
        span,
        valueSpan: labelled ? valueSpan : null,
      })
    }
    i = end
  }
  flush()
  return terms
}

const tenth = (value: number) => Math.round(value * 10) / 10
const whole = (value: number) => Math.round(value)

/** Whether a shown figure falls inside a bound, its ends rounded the way the figure was. */
function inside(shown: number, bound: Bound, round: (value: number) => number): boolean {
  const low = Number.isFinite(bound.low) ? round(bound.low) : bound.low
  const high = Number.isFinite(bound.high) ? round(bound.high) : bound.high
  return (
    (bound.lowOpen ? shown > low : shown >= low) && (bound.highOpen ? shown < high : shown <= high)
  )
}

function weightHolds(set: SearchableSet, bound: Bound, units: WeightUnits): boolean {
  if (set.weightKg === null) return false
  const shown = tenth(units === 'kg' ? set.weightKg : set.weightKg / KG_PER_LB)
  return inside(shown, bound, tenth)
}

function repsHold(set: SearchableSet, bound: Bound): boolean {
  return set.reps !== null && inside(set.reps, bound, (value) => value)
}

const SECONDS: Readonly<Record<string, number>> = { s: 1, min: 60, h: 3600 }

function timeHolds(set: SearchableSet, bound: Bound, unit: string): boolean {
  if (set.durationSeconds === null) return false
  const factor = SECONDS[unit] ?? 1
  const inSeconds = { ...bound, low: bound.low * factor, high: bound.high * factor }
  return inside(whole(set.durationSeconds), inSeconds, whole)
}

function distanceHolds(set: SearchableSet, bound: Bound, unit: string): boolean {
  const meters = set.distanceMeters
  if (meters === null) return false
  switch (unit) {
    case 'km':
      return inside(tenth(meters / 1000), bound, tenth)
    case 'mi':
      return inside(tenth(meters / METERS_PER_MILE), bound, tenth)
    case 'yd':
      return inside(whole(meters / METERS_PER_YARD), bound, whole)
    default:
      return inside(whole(meters), bound, whole)
  }
}

function operandHolds(set: SearchableSet, operand: Operand, context: SearchContext): boolean {
  const units = context.units
  const { bound, unit } = operand
  switch (operand.kind) {
    case 'weight':
      // No unit is the account's: what `weight:100` is written in.
      return weightHolds(set, bound, unit === '' ? units : unit === 'lb' ? 'lb' : 'kg')
    case 'reps':
      return repsHold(set, bound)
    case 'time':
      return timeHolds(set, bound, unit)
    case 'distance':
      return distanceHolds(set, bound, unit === '' ? (context.distanceUnits ?? 'km') : unit)
    case 'metresOrMinutes':
      return distanceHolds(set, bound, 'm') || timeHolds(set, bound, 'min')
    case 'effort':
      return set.rpe !== null && inside(tenth(set.rpe), bound, tenth)
    case 'bare':
      return (
        weightHolds(set, bound, units) ||
        repsHold(set, bound) ||
        // A whole number in the years a search reads is a year as well.
        (bound.low === bound.high &&
          !bound.lowOpen &&
          Number.isInteger(bound.low) &&
          bound.low >= 1900 &&
          bound.low <= 2099 &&
          localOf(set.performedAt, context.timeZone).year === bound.low)
      )
  }
}

/** A moment on the device's clock: its calendar day, weekday and hour. */
interface Local {
  readonly year: number
  readonly month: number
  readonly day: number
  /** 1 for Monday through 7 for Sunday. */
  readonly weekday: number
  readonly hour: number
}

const WEEKDAY_OF: Readonly<Record<string, number>> = {
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
  Sun: 7,
}

const clocks = new Map<string, Intl.DateTimeFormat>()

function localOf(at: number, timeZone: string): Local {
  let clock = clocks.get(timeZone)
  if (clock === undefined) {
    clock = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      hourCycle: 'h23',
    })
    clocks.set(timeZone, clock)
  }
  const parts = Object.fromEntries(clock.formatToParts(at).map((part) => [part.type, part.value]))
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    weekday: WEEKDAY_OF[parts.weekday ?? ''] ?? 1,
    hour: Number(parts.hour) % 24,
  }
}

/** Which day since the epoch a calendar day is, whatever the clock it was read on. */
function dayNumber(local: Local): number {
  return Math.floor(Date.UTC(local.year, local.month - 1, local.day) / 86_400_000)
}

/** The day number of the first day of the week a day falls in. */
function weekBegun(local: Local, firstDayOfWeek: number): number {
  return dayNumber(local) - ((local.weekday - firstDayOfWeek + 7) % 7)
}

const PARTS: Readonly<Record<PartOfDay, (hour: number) => boolean>> = {
  morning: (hour) => hour >= 5 && hour < 12,
  afternoon: (hour) => hour >= 12 && hour < 17,
  evening: (hour) => hour >= 17 && hour < 22,
  night: (hour) => hour >= 22 || hour < 5,
}

function momentHolds(set: SearchableSet, moment: Moment, context: SearchContext): boolean {
  const when = localOf(set.performedAt, context.timeZone)
  switch (moment.type) {
    case 'month':
      return when.month === moment.month && (moment.year === null || when.year === moment.year)
    case 'date':
      return (
        when.day === moment.day &&
        when.month === moment.month &&
        (moment.year === null || when.year === moment.year)
      )
    case 'year':
      return when.year === moment.year
    case 'weekday':
      return when.weekday === moment.weekday
    case 'partOfDay':
      return PARTS[moment.part](when.hour)
  }
  const today = localOf(context.now, context.timeZone)
  switch (moment.type) {
    case 'daysAgo':
      return dayNumber(when) === dayNumber(today) - moment.count
    case 'weeksAgo':
      return (
        weekBegun(when, context.firstDayOfWeek) ===
        weekBegun(today, context.firstDayOfWeek) - 7 * moment.count
      )
    case 'monthsAgo':
      return when.year * 12 + when.month === today.year * 12 + today.month - moment.count
    case 'yearsAgo':
      return when.year === today.year - moment.count
  }
}

/** Everything a set says — see the spec, What a set says. */
function sayings(set: SearchableSet): (string | null | undefined)[] {
  return [
    set.exerciseName,
    set.notes,
    ...(set.songs ?? []).flatMap((song) => [song.title, song.artist, song.album]),
    set.workoutName,
    ...(set.workoutNotes ?? []),
    set.templateName,
    set.templateWorkout,
  ]
}

function says(set: SearchableSet, text: string): boolean {
  const wanted = compact(text)
  return (
    wanted !== '' &&
    sayings(set).some((saying) => saying != null && compact(saying).includes(wanted))
  )
}

/** What one text label reads of a set — see the spec, Labels. */
function fieldOf(set: SearchableSet, field: TextLabel): (string | null | undefined)[] {
  const songs = set.songs ?? []
  switch (field) {
    case 'exercise':
      return [set.exerciseName]
    case 'note':
      return [set.notes, ...(set.workoutNotes ?? [])]
    case 'song':
      return songs.flatMap((song) => [song.title, song.artist, song.album])
    case 'artist':
      return songs.map((song) => song.artist)
    case 'album':
      return songs.map((song) => song.album)
    case 'workout':
      return [set.workoutName]
    case 'template':
      return [set.templateName, set.templateWorkout]
  }
}

/** Whether a set was performed on or after, or on or before, a day, a month or a year. */
function edgeHolds(
  set: SearchableSet,
  edge: 'after' | 'before',
  on: Edge,
  context: SearchContext,
): boolean {
  const done = dayNumber(localOf(set.performedAt, context.timeZone))
  const { year } = on
  if (edge === 'after') {
    const from = dayNumber({ year, month: on.month ?? 1, day: on.day ?? 1, weekday: 1, hour: 0 })
    return done >= from
  }
  const month = on.month ?? 12
  const until = dayNumber({ year, month, day: on.day ?? daysIn(month, year), weekday: 1, hour: 0 })
  return done <= until
}

/** Whether one term holds for a set, its negation aside — see the spec, Finding sets. */
function holds(term: Term, set: SearchableSet, context: SearchContext): boolean {
  // A labelled term holds only as its label says, never by what else the set says.
  if (term.label === null && says(set, term.text)) return true
  switch (term.kind) {
    case 'word':
      return false
    case 'field': {
      const wanted = compact(term.text)
      return fieldOf(set, term.field).some(
        (saying) => saying != null && compact(saying).includes(wanted),
      )
    }
    case 'edge':
      return edgeHolds(set, term.edge, term.on, context)
    case 'moment':
      return momentHolds(set, term.moment, context)
    case 'bodyweight':
      return set.reps !== null && set.weightKg === null
    case 'measure':
      return operandHolds(set, term.operand, context)
    case 'joined':
      return (
        term.beside.every((operand) => operandHolds(set, operand, context)) &&
        (term.pairings.length === 0 ||
          term.pairings.some(
            (pairing) =>
              repsHold(set, pairing.reps) &&
              weightHolds(set, pairing.weight, pairing.unit ?? context.units),
          ))
      )
  }
}

/** Whether a set is one the search finds: every term holding for it, on this device. */
export function findsSet(
  terms: readonly Term[],
  set: SearchableSet,
  context: SearchContext,
): boolean {
  // The same label more than once, none of them negated, is either of them.
  const either = new Map<string, Term[]>()
  for (const term of terms) {
    if (term.label !== null && !term.negated) {
      either.set(term.label, [...(either.get(term.label) ?? []), term])
    }
  }
  return (
    terms.every(
      (term) =>
        (term.label !== null && !term.negated) || holds(term, set, context) !== term.negated,
    ) && [...either.values()].every((group) => group.some((term) => holds(term, set, context)))
  )
}

/*
 * Saying how search works — `/specs/features/search.spec.md`. How a search was read, said back
 * in plain words; and the examples how search works is explained with, drawn from the account's
 * own history where it has them.
 */

const MONTH_SAID = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const WEEKDAY_SAID = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

/** A figure as a person writes it: whole where it is whole, and to a tenth where it is not. */
function figure(value: number): string {
  const toTenth = Math.round(value * 10) / 10
  return Number.isInteger(toTenth) ? String(toTenth) : toTenth.toFixed(1)
}

/** A bound in words, each number carrying [suffix]: `50–60 kg`, `100 kg or more`. */
function boundSaid(bound: Bound, suffix: (value: number) => string): string {
  const { low, high } = bound
  if (low === high) return suffix(low)
  if (Number.isFinite(low) && Number.isFinite(high)) {
    return `${figure(low)}–${suffix(high)}`
  }
  if (Number.isFinite(low)) {
    return bound.lowOpen ? `more than ${suffix(low)}` : `${suffix(low)} or more`
  }
  return bound.highOpen ? `less than ${suffix(high)}` : `${suffix(high)} or less`
}

const repsSuffix = (value: number) => `${figure(value)} ${value === 1 ? 'rep' : 'reps'}`

const inUnit = (name: string) => (value: number) => `${figure(value)} ${name}`

function operandSaid(operand: Operand, context: SearchContext): string {
  const { bound, unit } = operand
  switch (operand.kind) {
    case 'weight':
      return boundSaid(bound, inUnit(unit === '' ? context.units : unit === 'lb' ? 'lb' : 'kg'))
    case 'reps':
      return boundSaid(bound, repsSuffix)
    case 'time':
      return boundSaid(bound, inUnit(unit))
    case 'distance':
      return boundSaid(bound, inUnit(unit === '' ? (context.distanceUnits ?? 'km') : unit))
    case 'metresOrMinutes':
      return `${boundSaid(bound, inUnit('m'))} or ${boundSaid(bound, inUnit('min'))}`
    case 'effort':
      return `RPE ${boundSaid(bound, figure)}`
    case 'bare': {
      const yearLike =
        bound.low === bound.high &&
        Number.isInteger(bound.low) &&
        bound.low >= 1900 &&
        bound.low <= 2099
      if (yearLike) {
        return `the year ${figure(bound.low)}, or ${figure(bound.low)} as a weight or reps`
      }
      return `${boundSaid(bound, inUnit(context.units))} or ${boundSaid(bound, repsSuffix)}`
    }
  }
}

function momentSaid(moment: Moment): string {
  switch (moment.type) {
    case 'year':
      return String(moment.year)
    case 'month':
      return [MONTH_SAID[moment.month - 1] ?? '', moment.year === null ? null : String(moment.year)]
        .filter((part) => part !== null)
        .join(' ')
    case 'date':
      return [
        String(moment.day),
        MONTH_SAID[moment.month - 1] ?? '',
        moment.year === null ? null : String(moment.year),
      ]
        .filter((part) => part !== null)
        .join(' ')
    case 'weekday':
      return `${WEEKDAY_SAID[moment.weekday - 1] ?? ''}s`
    case 'partOfDay':
      return `${moment.part}s`
    case 'daysAgo':
      return moment.count === 0
        ? 'today'
        : moment.count === 1
          ? 'yesterday'
          : `${String(moment.count)} days ago`
    case 'weeksAgo':
    case 'monthsAgo':
    case 'yearsAgo': {
      const unit =
        moment.type === 'weeksAgo' ? 'week' : moment.type === 'monthsAgo' ? 'month' : 'year'
      if (moment.count === 0) return `this ${unit}`
      if (moment.count === 1) return `last ${unit}`
      return `${String(moment.count)} ${unit}s ago`
    }
  }
}

/** How a search was read, a phrase per term in the order the terms were typed. */
export function describeSearch(terms: readonly Term[], context: SearchContext): string[] {
  return terms.map((term) => {
    // A label's name before what it read, and not before a term that was negated.
    const labelSaid = term.label === null || term.label === 'is' ? '' : `${term.label} `
    return `${term.negated ? 'not ' : ''}${labelSaid}${readingSaid(term, context)}`
  })
}

function edgeSaid(on: Edge): string {
  return [
    on.day === null ? null : String(on.day),
    on.month === null ? null : (MONTH_SAID[on.month - 1] ?? ''),
    String(on.year),
  ]
    .filter((part) => part !== null)
    .join(' ')
}

function readingSaid(term: Reading, context: SearchContext): string {
  {
    switch (term.kind) {
      case 'word':
      case 'field':
        return `“${term.text}”`
      case 'edge':
        return edgeSaid(term.on)
      case 'bodyweight':
        return 'bodyweight'
      case 'moment':
        return momentSaid(term.moment)
      case 'measure':
        return operandSaid(term.operand, context)
      case 'joined': {
        const pairs = [
          ...new Set(
            term.pairings.map(
              (pairing) =>
                `${boundSaid(pairing.reps, repsSuffix)} at ${boundSaid(
                  pairing.weight,
                  inUnit(pairing.unit ?? context.units),
                )}`,
            ),
          ),
        ]
        return [pairs.join(' or '), ...term.beside.map((operand) => operandSaid(operand, context))]
          .filter((part) => part !== '')
          .join(' · ')
      }
    }
  }
}

/** The examples how search works is explained with, by the kind of term each shows. */
export interface SearchExamples {
  readonly words: readonly string[]
  readonly weightAndReps: readonly string[]
  readonly timeDistanceEffort: readonly string[]
  readonly bodyweight: readonly string[]
  readonly when: readonly string[]
  readonly labels: readonly string[]
}

/** What stands in for an example the account's history holds none of. */
export const STAND_INS = {
  words: ['bench press', 'daft punk', 'leg day'],
  weightAndReps: ['squat 100kg', '5x50kg', '100+kg'],
  timeDistanceEffort: ['60s', '5km', '@8'],
  bodyweight: ['pull up bw'],
  when: ['march 2024', 'monday morning', 'last week'],
  labels: ['exercise:"bench press"', 'on:"last week"', '-artist:"daft punk"'],
} as const satisfies SearchExamples

/**
 * The key held by the most of [sets], and among keys held as often the one held most recently
 * — or null where no set holds one.
 */
function mostOften(
  sets: readonly SearchableSet[],
  keysOf: (set: SearchableSet) => readonly string[],
): string | null {
  const held = new Map<string, { count: number; latest: number }>()
  for (const each of sets) {
    for (const key of new Set(keysOf(each))) {
      const was = held.get(key) ?? { count: 0, latest: -Infinity }
      held.set(key, { count: was.count + 1, latest: Math.max(was.latest, each.performedAt) })
    }
  }
  let best: string | null = null
  let bestCount = 0
  let bestLatest = -Infinity
  for (const [key, { count, latest }] of held) {
    if (count > bestCount || (count === bestCount && latest > bestLatest)) {
      best = key
      bestCount = count
      bestLatest = latest
    }
  }
  return best
}

/** The latest of [sets] that [has] holds for, or undefined where none does. */
function latestOf(
  sets: readonly SearchableSet[],
  has: (set: SearchableSet) => boolean,
): SearchableSet | undefined {
  let latest: SearchableSet | undefined
  for (const each of sets) {
    if (has(each) && (latest === undefined || each.performedAt > latest.performedAt)) {
      latest = each
    }
  }
  return latest
}

function partOfDayAt(hour: number): PartOfDay {
  if (hour >= 5 && hour < 12) return 'morning'
  if (hour >= 12 && hour < 17) return 'afternoon'
  if (hour >= 17 && hour < 22) return 'evening'
  return 'night'
}

/**
 * The examples of how search works, drawn from [history] where it holds one of each kind and
 * stood in for where it holds none — see the spec, Saying how search works.
 */
export function examplesOf(
  history: readonly SearchableSet[],
  context: SearchContext,
): SearchExamples {
  const units = context.units
  const shown = (kg: number) => tenth(units === 'kg' ? kg : kg / KG_PER_LB)
  const lower = (text: string) => text.trim().toLowerCase()

  const exercise = mostOften(history, (each) => [lower(each.exerciseName)])
  const artist = mostOften(history, (each) =>
    (each.songs ?? []).flatMap((song) => (song.artist == null ? [] : [lower(song.artist)])),
  )
  const partOf = latestOf(history, (each) => each.workoutName != null || each.templateName != null)

  const lifted = history.filter((each) => each.weightKg !== null)
  const pairing = mostOften(lifted, (each) => [
    `${lower(each.exerciseName)} ${figure(shown(each.weightKg ?? 0))}${units}`,
  ])
  const latestPair = latestOf(history, (each) => each.weightKg !== null && each.reps !== null)
  const heaviest = lifted.reduce((most, each) => Math.max(most, shown(each.weightKg ?? 0)), 0)

  const timed = latestOf(history, (each) => each.durationSeconds !== null)
  const run = latestOf(history, (each) => each.distanceMeters !== null)
  const effort = mostOften(history, (each) => (each.rpe === null ? [] : [figure(tenth(each.rpe))]))
  const bodyweight = latestOf(history, (each) => each.reps !== null && each.weightKg === null)

  let first: SearchableSet | undefined
  for (const each of history) {
    if (first === undefined || each.performedAt < first.performedAt) first = each
  }
  const firstLocal = first === undefined ? null : localOf(first.performedAt, context.timeZone)
  const habit = mostOften(history, (each) => {
    const local = localOf(each.performedAt, context.timeZone)
    return [`${lower(WEEKDAY_SAID[local.weekday - 1] ?? '')} ${partOfDayAt(local.hour)}`]
  })

  return {
    words: [
      exercise ?? STAND_INS.words[0],
      artist ?? STAND_INS.words[1],
      partOf === undefined
        ? STAND_INS.words[2]
        : lower(partOf.workoutName ?? partOf.templateName ?? ''),
    ],
    weightAndReps: [
      pairing ?? STAND_INS.weightAndReps[0],
      latestPair === undefined
        ? STAND_INS.weightAndReps[1]
        : `${String(latestPair.reps)}x${figure(shown(latestPair.weightKg ?? 0))}${units}`,
      heaviest >= 10
        ? `${String(Math.floor(heaviest / 10) * 10)}+${units}`
        : STAND_INS.weightAndReps[2],
    ],
    timeDistanceEffort: [
      timed === undefined
        ? STAND_INS.timeDistanceEffort[0]
        : `${String(whole(timed.durationSeconds ?? 0))}s`,
      run === undefined
        ? STAND_INS.timeDistanceEffort[1]
        : `${figure(tenth((run.distanceMeters ?? 0) / 1000))}km`,
      effort === null ? STAND_INS.timeDistanceEffort[2] : `@${effort}`,
    ],
    bodyweight: [
      bodyweight === undefined ? STAND_INS.bodyweight[0] : `${lower(bodyweight.exerciseName)} bw`,
    ],
    when: [
      firstLocal === null
        ? STAND_INS.when[0]
        : `${lower(MONTH_SAID[firstLocal.month - 1] ?? '')} ${String(firstLocal.year)}`,
      habit ?? STAND_INS.when[1],
      STAND_INS.when[2],
    ],
    labels: [
      labelWritten('exercise', exercise ?? STAND_INS.words[0]),
      STAND_INS.labels[1],
      `-${labelWritten('artist', artist ?? STAND_INS.words[1])}`,
    ],
  }
}

/*
 * Filters — the search spec, Filters. A filter reads what the search says of its labels and
 * writes its own into it; the search itself stays the one place a search is said.
 */

/** Which labels each filter writes. */
export const FILTER_LABELS = {
  when: ['on', 'after', 'before'],
  exercise: ['exercise'],
  workout: ['workout'],
  template: ['template'],
  artist: ['artist'],
  weight: ['weight'],
  reps: ['reps'],
} as const satisfies Readonly<Record<string, readonly string[]>>

export type FilterName = keyof typeof FILTER_LABELS

/** A label a search holds, its value as it was typed. */
export interface LabelHeld {
  readonly label: string
  readonly value: string
  readonly negated: boolean
}

/**
 * What the weight and reps filters write from a lowest and a highest, either left empty: a
 * range, that much or more, or that much or less — or null where both are empty.
 */
export function rangeWritten(lowest: string, highest: string): string | null {
  const low = lowest.trim()
  const high = highest.trim()
  if (low !== '' && high !== '') return `${low}-${high}`
  if (low !== '') return `${low}+`
  if (high !== '') return `<=${high}`
  return null
}

/** The lowest and highest a range the filters write says, empty where it says none or is not one. */
export function rangeRead(value: string): { lowest: string; highest: string } {
  const number = '(\\d+(?:\\.\\d+)?)'
  const range = new RegExp(`^${number}-${number}$`).exec(value)
  if (range !== null) return { lowest: range[1] ?? '', highest: range[2] ?? '' }
  const more = new RegExp(`^${number}\\+$`).exec(value)
  if (more !== null) return { lowest: more[1] ?? '', highest: '' }
  const less = new RegExp(`^<=${number}$`).exec(value)
  if (less !== null) return { lowest: '', highest: less[1] ?? '' }
  return { lowest: '', highest: '' }
}

/** A day as `after:` and `before:` are written with it, in the order the device writes a date. */
export function dayWritten(year: number, month: number, day: number, dayFirst: boolean): string {
  return dayFirst ? `${day}/${month}/${year}` : `${month}/${day}/${year}`
}

/** A label and its value as a search writes them, in quotes where the value has a space in it. */
export function labelWritten(label: string, value: string): string {
  const bare = value.replace(/"/g, '').trim()
  return /\s/.test(bare) ? `${label}:"${bare}"` : `${label}:${bare}`
}

/** Every label a search holds that could be read, in the order they were written. */
export function labelsIn(
  search: string,
  options: ReadingOptions = { dayFirst: true },
): LabelHeld[] {
  const held: LabelHeld[] = []
  for (const term of readSearch(search, options)) {
    if (term.label === null || term.valueSpan === null) continue
    held.push({
      label: term.label,
      value: search.slice(term.valueSpan.start, term.valueSpan.end).trim(),
      negated: term.negated,
    })
  }
  return held
}

/**
 * The search with [filter]'s own labels taken out and [labels] written at its end. A negated
 * label is not the filter's to take: it stays where it was written.
 */
export function withFilter(
  search: string,
  filter: FilterName,
  labels: readonly { readonly label: string; readonly value: string }[],
  options: ReadingOptions = { dayFirst: true },
): string {
  const own: readonly string[] = FILTER_LABELS[filter]
  const spans = readSearch(search, options)
    .filter((term) => term.label !== null && !term.negated && own.includes(term.label))
    .flatMap((term) => (term.span === null ? [] : [term.span]))
    .sort((a, b) => b.start - a.start)
  let kept = search
  for (const span of spans) {
    kept = `${kept.slice(0, span.start)} ${kept.slice(span.end)}`
  }
  return [kept, ...labels.map((each) => labelWritten(each.label, each.value))]
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
}
